import React, { useState } from "react";
import "./PatientAvatar.css";

/**
 * The patient's picture, or a stand-in drawn from what the record does say.
 *
 * Most patient records have no photo — the desk never asks for one — so the
 * fallback is not a generic grey bust but an illustration chosen from the two
 * facts the record almost always carries: gender and how old the patient is.
 * That is enough for a person holding two records on one mobile number to tell
 * them apart at a glance, which is the whole job of the header and the record
 * picker.
 */

/**
 * Age in whole years, or null when the record says nothing usable.
 *
 * `dateOfBirth` is authoritative when present. A desk-created record usually
 * has none and carries `age` instead — a STRING typed by reception, which in
 * the wild reads "30", "30 yrs", "3 months" or "18 Months". Anything under a
 * year is 0, not null: an infant is a real answer.
 */
export function patientAgeYears(patient) {
  const dob = patient?.dateOfBirth;
  if (dob) {
    const born = new Date(dob);
    if (!Number.isNaN(born.getTime())) {
      const now = new Date();
      let years = now.getFullYear() - born.getFullYear();
      const monthDelta = now.getMonth() - born.getMonth();
      if (
        monthDelta < 0 ||
        (monthDelta === 0 && now.getDate() < born.getDate())
      ) {
        years -= 1;
      }
      if (years >= 0 && years < 130) return years;
    }
  }

  const raw = String(patient?.age ?? "").trim();
  if (!raw) return null;
  const number = parseFloat(raw);
  if (Number.isNaN(number)) return null;
  if (/month|week|day|\bm\b|\bw\b|\bd\b/i.test(raw)) {
    return /month/i.test(raw) ? Math.floor(number / 12) : 0;
  }
  return number >= 0 && number < 130 ? Math.floor(number) : null;
}

/** Which illustration to draw: a gender key and an age band. */
export function avatarLook(patient) {
  const gender = String(patient?.gender ?? "").toUpperCase();
  const years = patientAgeYears(patient);
  return {
    gender:
      gender === "MALE" ? "male" : gender === "FEMALE" ? "female" : "other",
    // Unknown age draws the adult — the commonest patient, and never a wrong
    // guess the patient can read as the clinic thinking them a child.
    band:
      years == null
        ? "adult"
        : years < 13
          ? "child"
          : years >= 60
            ? "senior"
            : "adult",
  };
}

const PALETTE = {
  male: { bg: "#dbeafe", cloth: "#2563eb", hair: "#334155" },
  female: { bg: "#fce7f3", cloth: "#db2777", hair: "#4b2e2e" },
  other: { bg: "#ede9fe", cloth: "#7c3aed", hair: "#3f3f46" },
};

const SKIN = "#f3c9a4";
const SKIN_SHADE = "#e3b189";
const INK = "#1f2937";

const PatientAvatar = ({ patient, size = 40, className = "" }) => {
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = patient?.photo;
  const { gender, band } = avatarLook(patient);
  const colors = PALETTE[gender];
  const hair = band === "senior" ? "#cbd5e1" : colors.hair;
  const label = patient?.name ? `${patient.name}'s picture` : "Patient";

  const style = { width: size, height: size };

  if (photo && !photoFailed) {
    return (
      <span className={`patient_avatar ${className}`} style={style}>
        <img src={photo} alt={label} onError={() => setPhotoFailed(true)} />
      </span>
    );
  }

  return (
    <span className={`patient_avatar ${className}`} style={style}>
      <svg
        viewBox="0 0 64 64"
        role="img"
        aria-label={label}
        data-look={`${gender}-${band}`}
      >
        <circle cx="32" cy="32" r="32" fill={colors.bg} />
        {/* shoulders, then neck, then head — painted back to front */}
        <path d="M12 64c0-10 9-16 20-16s20 6 20 16H12Z" fill={colors.cloth} />
        <rect x="27" y="34" width="10" height="12" rx="5" fill={SKIN_SHADE} />
        <circle cx="32" cy="27" r={band === "child" ? 13 : 12} fill={SKIN} />
        {gender === "female" ? (
          <path
            d="M19 27a13 13 0 0 1 26 0v9a3 3 0 0 1-6 0v-6H25v6a3 3 0 0 1-6 0v-9Z"
            fill={hair}
          />
        ) : band === "child" ? (
          <>
            <path d="M21 27a11 11 0 0 1 22 0Z" fill={hair} />
            <path
              d="M32 12c3.3 0 5.5 2 5.5 4.2-2.2-2.2-8.8-2.2-11 0C26.5 14 28.7 12 32 12Z"
              fill={hair}
            />
          </>
        ) : (
          <path d="M19 27a13 13 0 0 1 26 0Z" fill={hair} />
        )}
        <circle cx="28" cy="28" r="1.5" fill={INK} />
        <circle cx="36" cy="28" r="1.5" fill={INK} />
        <path
          d="M28.5 33.5c1.2 1.4 5.8 1.4 7 0"
          stroke={INK}
          strokeWidth="1.4"
          strokeLinecap="round"
          fill="none"
        />
        {band === "senior" && (
          <g stroke={INK} strokeWidth="1.2" fill="none" opacity="0.8">
            <circle cx="28" cy="28" r="4.2" />
            <circle cx="36" cy="28" r="4.2" />
            <path d="M32.2 28h-0.4" />
          </g>
        )}
      </svg>
    </span>
  );
};

export default PatientAvatar;
